import React, { useState, useContext, useEffect } from 'react';
import { Modal, Badge, Row, Col } from 'react-bootstrap';
import {
  MapPin, Thermometer, Clock, DollarSign,
  Image as ImageIcon, Navigation, Tag,
} from 'lucide-react';
import AppContext from '../../../context/AppContext';

const InfoRow = ({ label, value }) => (
  <div className="dest-detail-info-row">
    <span className="dest-detail-label">{label}</span>
    <span className="dest-detail-value">{value || 'N/A'}</span>
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="dest-detail-section">
    <h5 className="dest-detail-section-title">
      {Icon && <Icon size={18} className="me-2" />}{title}
    </h5>
    <div className="dest-detail-section-body">{children}</div>
  </div>
);

const DestinationDetailsModal = ({ show, onHide, destination }) => {
  const { imageBaseUrl } = useContext(AppContext);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setActivePhoto(0);
  }, [destination]);

  if (!destination) return null;

  const resolvePath = (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${imageBaseUrl}/${path}`;
  };

  const name     = destination.destination_name || destination.name || 'Destination';
  const district = destination.district || 'N/A';
  const province = destination.province || 'N/A';
  const city     = destination.nearest_city || 'N/A';
  const lat      = destination.latitude || 'N/A';
  const lng      = destination.longitude || 'N/A';
  const climate  = destination.climate || 'N/A';
  const season   = destination.best_season || 'N/A';
  const duration = destination.recommended_duration || 'N/A';
  const minBud   = destination.min_budget;
  const maxBud   = destination.max_budget;
  const budgetStr = minBud && maxBud
    ? `PKR ${Number(minBud).toLocaleString()} – ${Number(maxBud).toLocaleString()}`
    : 'N/A';
  const desc     = destination.description || '';
  const status   = destination.status || 'Active';
  const spots    = destination.jjdestinationspots ?? [];
  const gallery  = destination.jjdestinationgallerys ?? [];

  const galleryUrls = gallery
    .map((g) => resolvePath(g.des_gallery))
    .filter(Boolean);

  const primaryImageUrl = resolvePath(destination.image);

  const allPhotos = primaryImageUrl
    ? [primaryImageUrl, ...galleryUrls.filter((u) => u !== primaryImageUrl)]
    : galleryUrls;

  const heroPhoto = allPhotos.length > 0 ? allPhotos[activePhoto] : null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="dest-detail-modal">
      <Modal.Header closeButton className="dest-detail-modal-header">
        <div className="d-flex align-items-center gap-3 w-100 flex-wrap">
          <div>
            <Modal.Title className="dest-detail-modal-title">{name}</Modal.Title>
            <div className="dest-detail-location-line">
              <MapPin size={14} />
              <span>{district}, {province}</span>
            </div>
          </div>
          <Badge bg={status === 'Active' ? 'success' : 'danger'} className="ms-auto">
            {status}
          </Badge>
        </div>
      </Modal.Header>

      <Modal.Body className="dest-detail-modal-body p-0">

        {heroPhoto ? (
          <div className="dest-detail-hero-wrapper">
            <img src={heroPhoto} alt={name} className="dest-detail-hero-img" />
            <div className="dest-detail-hero-overlay">
              <span className="dest-detail-hero-name">{name}</span>
            </div>
          </div>
        ) : (
          <div className="dest-detail-hero-wrapper d-flex align-items-center justify-content-center bg-light">
            <ImageIcon size={48} className="text-muted" />
          </div>
        )}

        {allPhotos.length > 1 && (
          <div className="dest-detail-thumbs-bar">
            {allPhotos.map((url, idx) => (
              <img
                key={idx}
                src={url}
                alt={`Photo ${idx + 1}`}
                className={`dest-detail-thumb ${activePhoto === idx ? 'active' : ''}`}
                onClick={() => setActivePhoto(idx)}
              />
            ))}
          </div>
        )}

        <div className="dest-detail-content p-4">

          <div className="dest-detail-stats-bar mb-4">
            <div className="dest-detail-stat">
              <Clock size={20} />
              <div>
                <span className="dest-detail-stat-label">Duration</span>
                <span className="dest-detail-stat-value">{duration}</span>
              </div>
            </div>
            <div className="dest-detail-stat">
              <Thermometer size={20} />
              <div>
                <span className="dest-detail-stat-label">Climate</span>
                <span className="dest-detail-stat-value">{climate}</span>
              </div>
            </div>
            <div className="dest-detail-stat">
              <DollarSign size={20} />
              <div>
                <span className="dest-detail-stat-label">Budget</span>
                <span className="dest-detail-stat-value">{budgetStr}</span>
              </div>
            </div>
            <div className="dest-detail-stat">
              <Navigation size={20} />
              <div>
                <span className="dest-detail-stat-label">Best Season</span>
                <span className="dest-detail-stat-value">{season}</span>
              </div>
            </div>
          </div>

          {desc && (
            <Section title="About this Destination">
              <p className="dest-detail-description">{desc}</p>
            </Section>
          )}

          <Row>
            <Col md={6}>
              <Section icon={MapPin} title="Location Details">
                <InfoRow label="Province"     value={province} />
                <InfoRow label="District"     value={district} />
                <InfoRow label="Nearest City" value={city}     />
                <InfoRow label="Coordinates"  value={lat !== 'N/A' ? `${lat}, ${lng}` : 'N/A'} />
              </Section>
            </Col>

            <Col md={6}>
              <Section icon={Thermometer} title="Travel Info">
                <InfoRow label="Climate"           value={climate}  />
                <InfoRow label="Best Season"       value={season}   />
                <InfoRow label="Recommended Stay"  value={duration} />
                <InfoRow label="Budget Range"      value={budgetStr}/>
              </Section>
            </Col>
          </Row>

          {spots.length > 0 && (
            <Section icon={Tag} title={`Tourist Spots (${spots.length})`}>
              <div className="dest-detail-spots-grid">
                {spots.map((spot, idx) => {
                  const spotImg = resolvePath(spot.spot_image);
                  return (
                    <div key={idx} className="dest-detail-spot-card">
                      {spotImg && (
                        <img
                          src={spotImg}
                          alt={spot.spot_title}
                          className="dest-detail-spot-img"
                        />
                      )}
                      <div className="dest-detail-spot-body">
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                          <h6 className="dest-detail-spot-title mb-0">
                            {idx + 1}. {spot.spot_title || spot.title || 'Unnamed Spot'}
                          </h6>
                          {spot.category && (
                            <Badge bg="info" className="flex-shrink-0">{spot.category}</Badge>
                          )}
                        </div>
                        {spot.spot_description || spot.description ? (
                          <p className="dest-detail-spot-desc mb-1">
                            {spot.spot_description || spot.description}
                          </p>
                        ) : null}
                        <div className="dest-detail-spot-meta">
                          {spot.visit_duration && (
                            <span><Clock size={12} /> {spot.visit_duration} min</span>
                          )}
                          {spot.latitude && spot.longitude && (
                            <span><MapPin size={12} /> {spot.latitude}, {spot.longitude}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {allPhotos.length > 0 && (
            <Section icon={ImageIcon} title={`Photo Gallery (${allPhotos.length})`}>
              <div className="dest-detail-gallery-grid">
                {allPhotos.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Gallery ${idx + 1}`}
                    className={`dest-detail-gallery-thumb ${activePhoto === idx ? 'ring' : ''}`}
                    onClick={() => setActivePhoto(idx)}
                  />
                ))}
              </div>
            </Section>
          )}

        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DestinationDetailsModal;